import type { ReaderRouteSection, LoadedSectionPart, LoadedSection } from '@arianna/reader-core'

// ---------------------------------------------------------------------------
// Section types — canonical definitions live in @arianna/reader-core.
// Re-exported here under the reader-render names for backward compatibility.
// ---------------------------------------------------------------------------

/** @alias ReaderRouteSection */
export type ReaderRenderSection = ReaderRouteSection

/** @alias LoadedSectionPart */
export type ReaderRenderSectionPart = LoadedSectionPart

/** @alias LoadedSection */
export type ReaderRenderLoadedSection = LoadedSection

// ---------------------------------------------------------------------------
// Highlight type (platform-agnostic, no dependency on SQLite row types)
// ---------------------------------------------------------------------------

export const HIGHLIGHT_COLORS = [
  { color: 'yellow', label: 'Ambra', hex: '#b88a3a' },
  { color: 'green', label: 'Verde', hex: '#5a7a3e' },
  { color: 'pink', label: 'Rosa', hex: '#be5675' },
  { color: 'blue', label: 'Blu', hex: '#3e5a7a' },
  { color: 'red', label: 'Corallo', hex: '#e85d4f' },
  { color: 'purple', label: 'Viola', hex: '#9b5de5' },
  { color: 'teal', label: 'Turchese', hex: '#00a896' },
  { color: 'slate', label: 'Indaco', hex: '#5c7cfa' },
] as const

export const HIGHLIGHT_STYLES = [
  { style: 'mark', label: 'Evidenzia' },
  { style: 'underline', label: 'Sottolinea' },
] as const

export const DEFAULT_HIGHLIGHT_STYLE = 'mark' as const

export type ReaderRenderHighlightColor = (typeof HIGHLIGHT_COLORS)[number]['color']
export type ReaderRenderHighlightStyle = (typeof HIGHLIGHT_STYLES)[number]['style']

export interface ReaderRenderHighlight {
  id: string
  subParaId: string
  blockId: string
  startChar: number
  endChar: number
  color: ReaderRenderHighlightColor
  style?: ReaderRenderHighlightStyle
}

// ---------------------------------------------------------------------------
// Bridge abstraction
// ---------------------------------------------------------------------------

/**
 * Platform-agnostic bridge interface.
 *
 * A `ReaderBridge` knows how to produce the JS snippet (as a string) that
 * implements message delivery inside the WebView sandbox. The snippet is
 * embedded verbatim inside the <script> block of the generated HTML so it
 * runs in the WebView context — there is no JSI/React Native bridge crossing
 * at call time; the choice of delivery mechanism is baked in at HTML-build
 * time.
 */
export interface ReaderBridge {
  /**
   * Returns a JS expression (no trailing semicolon needed) that delivers a
   * `payload` variable (already available in scope) to the host.
   *
   * Example for RN: `window.ReactNativeWebView.postMessage(JSON.stringify(payload))`
   * Example for web callback: `window.__ariannaPostMessage && window.__ariannaPostMessage(payload)`
   */
  readonly postExpression: string
}

// ---------------------------------------------------------------------------
// Theme type
// ---------------------------------------------------------------------------

/** The two supported reader themes. Matches ThemeName from @arianna/design-system. */
export type ReaderTheme = 'studiolo' | 'notturno'

// ---------------------------------------------------------------------------
// Main input type
// ---------------------------------------------------------------------------

export interface ReaderHtmlInput {
  section: ReaderRenderSection
  loadedSection: ReaderRenderLoadedSection
  highlights: ReadonlyArray<ReaderRenderHighlight>
  bridge: ReaderBridge
  /**
   * Visual theme applied to the WebView via `data-theme` on the `<html>` element.
   * Defaults to `'studiolo'` when omitted.
   */
  theme?: ReaderTheme
  /**
   * Abilita il glifo di margine `✦` per blocco e l'evento bridge
   * `block-action` (micro-menu mobile, Fase 8b — sostituisce il
   * `block-longpress` della Fase 8, scartato perché il long-press sul testo
   * dentro la WKWebView doveva restare la selezione nativa di sistema).
   * Default `true` — il consumer mobile (apps/reader) non deve passare nulla.
   * Nessun consumer web/desktop attuale usa `buildReaderHtml` in produzione
   * (solo test), quindi il default non rischia regressioni sul web.
   */
  blockActions?: boolean
}
